export type TenantSchool = {
  schoolId: string;
  schoolName: string;
  tenantId: string;
};

export type SchoolContextStatus =
  | 'unsupported'
  | 'loading'
  | 'ready'
  | 'selection-required'
  | 'no-membership'
  | 'error';

export type SchoolContextState = {
  activeSchoolId: string | null;
  schools: TenantSchool[] | null;
  status: SchoolContextStatus;
  isLoading: boolean;
  isResolving: boolean;
  isBlocked: boolean;
  needsSelection: boolean;
  hasMembership: boolean;
  error: string | null;
  selectSchool: (schoolId: string) => Promise<void>;
};

type DeriveSchoolContextStateInput = {
  canUseSchoolContext: boolean;
  activeSchoolId: string | null;
  schools: TenantSchool[] | null;
  isLoadingQueries: boolean;
  isSwitching: boolean;
  error: string | null;
};

export const deriveSchoolContextState = ({
  canUseSchoolContext,
  activeSchoolId,
  schools,
  isLoadingQueries,
  isSwitching,
  error,
}: DeriveSchoolContextStateInput): Omit<SchoolContextState, 'selectSchool'> => {
  if (!canUseSchoolContext) {
    return {
      activeSchoolId,
      schools,
      status: 'unsupported',
      isLoading: false,
      isResolving: false,
      isBlocked: false,
      needsSelection: false,
      hasMembership: false,
      error: null,
    };
  }

  if (error) {
    return {
      activeSchoolId,
      schools,
      status: 'error',
      isLoading: false,
      isResolving: false,
      isBlocked: false,
      needsSelection: false,
      hasMembership: Boolean(schools?.length),
      error,
    };
  }

  if (activeSchoolId) {
    return {
      activeSchoolId,
      schools,
      status: 'ready',
      isLoading: false,
      isResolving: false,
      isBlocked: false,
      needsSelection: false,
      hasMembership: true,
      error: null,
    };
  }

  if (isLoadingQueries || isSwitching || schools === null) {
    return {
      activeSchoolId,
      schools,
      status: 'loading',
      isLoading: true,
      isResolving: true,
      isBlocked: false,
      needsSelection: false,
      hasMembership: Boolean(schools?.length),
      error: null,
    };
  }

  if (schools.length === 0) {
    return {
      activeSchoolId,
      schools,
      status: 'no-membership',
      isLoading: false,
      isResolving: false,
      isBlocked: true,
      needsSelection: false,
      hasMembership: false,
      error: null,
    };
  }

  return {
    activeSchoolId,
    schools,
    status: 'selection-required',
    isLoading: false,
    isResolving: false,
    isBlocked: true,
    needsSelection: true,
    hasMembership: true,
    error: null,
  };
};
